import React, { useState, useEffect, useCallback } from 'react';
import {
  GlobeAltIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';
import { pimService } from '../../services/pimService';
import apiService from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

interface ProductTranslation {
  id: number;
  product_id: number;
  locale: string;
  name: string;
  description?: string;
  slug?: string;
  meta_title?: string;
  meta_description?: string;
  created_at: string;
  updated_at: string;
}

interface Product {
  id: number;
  name: string;
  sku: string;
  status: string;
  translations?: ProductTranslation[];
}

const SUPPORTED_LOCALES = [
  { code: 'en', name: 'English', flag: '🇬🇧', nativeName: 'English' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱', nativeName: 'Polski' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'es', name: 'Español', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', nativeName: 'Italiano' }
];

interface TranslationFormData {
  locale: string;
  name: string;
  description: string;
  slug: string;
  meta_title: string;
  meta_description: string;
}

export const Translations: React.FC = () => {
  const { canManagePim, canWriteProducts, canReadProducts } = usePermissions();
  const { showNotification } = useNotification();

  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [translations, setTranslations] = useState<ProductTranslation[]>([]);
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState<ProductTranslation | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingTranslation, setSavingTranslation] = useState(false);
  const [formData, setFormData] = useState<TranslationFormData>({
    locale: 'en',
    name: '',
    description: '',
    slug: '',
    meta_title: '',
    meta_description: ''
  });

  useEffect(() => {
    if (canReadProducts) {
      loadProducts();
    }
  }, [canReadProducts]);

  useEffect(() => {
    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchQuery, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await pimService.getProducts({ limit: 100 });
      setProducts(response.data || []);
      setFilteredProducts(response.data || []);
    } catch (error) {
      showNotification('Nie udało się załadować produktów', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadProductTranslations = async (productId: number) => {
    try {
      setLoading(true);
      const response = await apiService.get(`/pim/products/${productId}/translations`);
      setTranslations(response.data.data || []);
    } catch (error) {
      showNotification('Nie udało się załadować tłumaczeń', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    loadProductTranslations(product.id);
  };

  const generateSlug = useCallback((text: string) => {
    return text
      .toLowerCase()
      .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
      .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
      .replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z')
      .replace(/ö/g, 'o').replace(/ä/g, 'a').replace(/ü/g, 'u')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }, []);

  const handleCreateTranslation = () => {
    setEditingTranslation(null);

    // Find first locale that doesn't have a translation
    const availableLocales = SUPPORTED_LOCALES.filter(locale =>
      !translations.find(t => t.locale === locale.code)
    );
    const defaultLocale = availableLocales.length > 0 ? availableLocales[0].code : 'en';

    setFormData({
      locale: defaultLocale,
      name: '',
      description: '',
      slug: '',
      meta_title: '',
      meta_description: ''
    });
    setShowTranslationModal(true);
  };

  const handleCreateForLocale = (locale: string) => {
    setEditingTranslation(null);
    setFormData({
      locale,
      name: '',
      description: '',
      slug: '',
      meta_title: '',
      meta_description: ''
    });
    setShowTranslationModal(true);
  };

  const handleEditTranslation = (translation: ProductTranslation) => {
    setEditingTranslation(translation);
    setFormData({
      locale: translation.locale,
      name: translation.name,
      description: translation.description || '',
      slug: translation.slug || '',
      meta_title: translation.meta_title || '',
      meta_description: translation.meta_description || ''
    });
    setShowTranslationModal(true);
  };

  const handleDuplicateTranslation = (translation: ProductTranslation) => {
    const availableLocales = SUPPORTED_LOCALES.filter(locale =>
      !translations.find(t => t.locale === locale.code)
    );

    if (availableLocales.length === 0) {
      showNotification('Wszystkie języki mają już tłumaczenia', 'info');
      return;
    }

    setEditingTranslation(null);
    setFormData({
      locale: availableLocales[0].code,
      name: translation.name,
      description: translation.description || '',
      slug: '', // Slug will be auto-generated
      meta_title: translation.meta_title || '',
      meta_description: translation.meta_description || ''
    });
    setShowTranslationModal(true);
  };

  const handleSaveTranslation = async () => {
    if (!selectedProduct || !formData.name.trim()) {
      showNotification('Nazwa produktu jest wymagana', 'error');
      return;
    }

    try {
      setSavingTranslation(true);

      // Auto-generate slug if empty
      const dataToSave = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name)
      };

      await apiService.put(`/pim/products/${selectedProduct.id}/translations/${formData.locale}`, dataToSave);

      showNotification(editingTranslation ? 'Tłumaczenie zaktualizowane' : 'Tłumaczenie dodane', 'success');
      setShowTranslationModal(false);
      loadProductTranslations(selectedProduct.id);
    } catch (error: any) {
      showNotification(`Nie udało się zapisać tłumaczenia: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setSavingTranslation(false);
    }
  };

  const handleDeleteTranslation = async (translation: ProductTranslation) => {
    if (!selectedProduct) return;

    const localeInfo = getLocaleInfo(translation.locale);
    if (!confirm(`Czy na pewno chcesz usunąć tłumaczenie dla języka ${localeInfo.name}?`)) {
      return;
    }

    try {
      setLoading(true);
      await apiService.delete(`/pim/products/${selectedProduct.id}/translations/${translation.locale}`);
      showNotification('Tłumaczenie usunięte', 'success');
      loadProductTranslations(selectedProduct.id);
    } catch (error: any) {
      showNotification(`Nie udało się usunąć tłumaczenia: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportTranslations = async () => {
    if (!selectedProduct) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/pim/products/${selectedProduct.id}/translations/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedProduct.sku}_translations.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        showNotification('Tłumaczenia wyeksportowane pomyślnie', 'success');
      } else {
        throw new Error('Failed to export translations');
      }
    } catch (error) {
      showNotification('Nie udało się wyeksportować tłumaczeń', 'error');
    }
  };

  const handleImportTranslations = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedProduct) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/pim/products/${selectedProduct.id}/translations/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        const result = await response.json();
        showNotification(`Import zakończony: zaimportowano ${result.imported || 0} tłumaczeń`, 'success');
        loadProductTranslations(selectedProduct.id);
      } else {
        const result = await response.json();
        throw new Error(result.message || 'Failed to import translations');
      }
    } catch (error: any) {
      showNotification(`Nie udało się zaimportować tłumaczeń: ${error.message}`, 'error');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const getLocaleInfo = (localeCode: string) => {
    return SUPPORTED_LOCALES.find(locale => locale.code === localeCode) ||
           { code: localeCode, name: localeCode.toUpperCase(), flag: '🌐', nativeName: localeCode };
  };

  const getTranslationCompleteness = () => {
    if (!selectedProduct || translations.length === 0) return 0;
    const possibleTranslations = SUPPORTED_LOCALES.length;
    return Math.round((translations.length / possibleTranslations) * 100);
  };

  const getTranslationFieldsCompleteness = (translation: ProductTranslation) => {
    let filled = 0;
    const total = 5; // name, description, slug, meta_title, meta_description

    if (translation.name) filled++;
    if (translation.description) filled++;
    if (translation.slug) filled++;
    if (translation.meta_title) filled++;
    if (translation.meta_description) filled++;

    return Math.round((filled / total) * 100);
  };

  if (!canReadProducts) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Brak dostępu</h3>
          <p className="mt-1 text-sm text-gray-500">
            Nie masz uprawnień do zarządzania tłumaczeniami produktów
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <GlobeAltIcon className="h-8 w-8 mr-3 text-blue-600" />
            Tłumaczenia Produktów
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Zarządzaj wielojęzycznymi treściami dla swoich produktów
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Products List */}
        <div className="xl:col-span-1">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Produkty</h3>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Szukaj produktów..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
              {loading && !selectedProduct ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm">Ładowanie produktów...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p className="text-sm">Nie znaleziono produktów</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredProducts.map(product => (
                    <button
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 focus:outline-none transition-colors ${
                        selectedProduct?.id === product.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">{product.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">SKU: {product.sku}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          product.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {product.status === 'published' ? 'Opublikowany' : 'Szkic'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Translations Management */}
        <div className="xl:col-span-2">
          {selectedProduct ? (
            <div className="space-y-6">
              {/* Product Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold">{selectedProduct.name}</h3>
                    <p className="text-sm text-blue-100 mt-1">SKU: {selectedProduct.sku}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={handleExportTranslations}
                      className="flex items-center px-3 py-2 text-sm bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors backdrop-blur-sm"
                      title="Eksportuj tłumaczenia"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                      Export
                    </button>
                    <label className="flex items-center px-3 py-2 text-sm bg-white/20 hover:bg-white/30 text-white rounded-md cursor-pointer transition-colors backdrop-blur-sm" title="Importuj tłumaczenia">
                      <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                      Import
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleImportTranslations}
                        className="sr-only"
                      />
                    </label>
                    {canWriteProducts && (
                      <button
                        onClick={handleCreateTranslation}
                        className="flex items-center px-3 py-2 text-sm bg-white text-blue-600 rounded-md hover:bg-blue-50 transition-colors shadow-md"
                      >
                        <PlusIcon className="h-4 w-4 mr-1.5" />
                        Dodaj tłumaczenie
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-blue-100 mb-1.5">
                    <span>Postęp tłumaczeń</span>
                    <span className="font-medium text-white">{translations.length} / {SUPPORTED_LOCALES.length} języków</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2.5 backdrop-blur-sm">
                    <div
                      className="bg-white h-2.5 rounded-full transition-all duration-500 shadow-md"
                      style={{ width: `${getTranslationCompleteness()}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Translations Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {SUPPORTED_LOCALES.map(locale => {
                  const translation = translations.find(t => t.locale === locale.code);
                  const completeness = translation ? getTranslationFieldsCompleteness(translation) : 0;

                  return (
                    <div
                      key={locale.code}
                      className={`relative border rounded-lg p-3 transition-all duration-200 hover:shadow-lg group ${
                        translation
                          ? completeness === 100
                            ? 'border-green-400 bg-gradient-to-br from-green-50 to-green-100/50 hover:border-green-500'
                            : 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100/50 hover:border-yellow-500'
                          : 'border-gray-300 bg-white hover:border-blue-400 hover:shadow-blue-100'
                      }`}
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl leading-none">{locale.flag}</span>
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-gray-900">{locale.name}</span>
                            <span className="text-[10px] text-gray-500 uppercase tracking-wide">{locale.code}</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {translation && (
                          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleDuplicateTranslation(translation)}
                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors"
                              title="Duplikuj"
                            >
                              <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                            </button>
                            {canWriteProducts && (
                              <button
                                onClick={() => handleEditTranslation(translation)}
                                className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                                title="Edytuj"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {canManagePim && (
                              <button
                                onClick={() => handleDeleteTranslation(translation)}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
                                title="Usuń"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {translation ? (
                        <div className="space-y-1.5">
                          {/* Product Name */}
                          <div className="text-xs font-medium text-gray-900 line-clamp-1 min-h-[16px]">
                            {translation.name || <span className="text-gray-400 italic">Brak nazwy</span>}
                          </div>

                          {/* Completeness Bar */}
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1 bg-gray-200 rounded-full h-1 overflow-hidden">
                              <div
                                className={`h-1 rounded-full transition-all duration-500 ${
                                  completeness === 100 ? 'bg-green-500' : 'bg-yellow-500'
                                }`}
                                style={{ width: `${completeness}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-gray-600 font-semibold min-w-[28px] text-right">{completeness}%</span>
                          </div>

                          {/* Status Dots */}
                          <div className="flex items-center gap-1 pt-1">
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${translation.name ? 'bg-green-500' : 'bg-gray-300'}`}
                              title="Nazwa"
                            />
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${translation.description ? 'bg-green-500' : 'bg-gray-300'}`}
                              title="Opis"
                            />
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${translation.slug ? 'bg-green-500' : 'bg-gray-300'}`}
                              title="Slug"
                            />
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${translation.meta_title ? 'bg-green-500' : 'bg-gray-300'}`}
                              title="Meta Title"
                            />
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${translation.meta_description ? 'bg-green-500' : 'bg-gray-300'}`}
                              title="Meta Desc"
                            />
                            <span className="text-[9px] text-gray-500 ml-auto">
                              {new Date(translation.updated_at).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center py-3">
                          {canWriteProducts ? (
                            <button
                              onClick={() => handleCreateForLocale(locale.code)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors"
                            >
                              <PlusIcon className="h-3.5 w-3.5" />
                              Dodaj
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Brak</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Help Card */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <InformationCircleIcon className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-xs font-bold text-blue-900 mb-1.5">Szybkie wskazówki</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-blue-800">
                      <div className="flex items-center gap-1">
                        <span className="text-blue-600">•</span>
                        <span>Kliknij <strong>+</strong> aby dodać tłumaczenie</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-blue-600">•</span>
                        <span>Slug generuje się automatycznie</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-blue-600">•</span>
                        <span><strong>Export/Import</strong> - wymiana przez CSV</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-blue-600">•</span>
                        <span><DocumentDuplicateIcon className="h-3 w-3 inline" /> kopiuje do innego języka</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-16 text-center">
              <GlobeAltIcon className="mx-auto h-16 w-16 text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Wybierz produkt</h3>
              <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                Wybierz produkt z listy po lewej stronie, aby zarządzać jego tłumaczeniami na różne języki
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Translation Modal */}
      {showTranslationModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-slideUp">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between border-b border-blue-400">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getLocaleInfo(formData.locale).flag}</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {editingTranslation ? 'Edytuj tłumaczenie' : 'Nowe tłumaczenie'}
                  </h3>
                  <p className="text-sm text-blue-100">{getLocaleInfo(formData.locale).name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowTranslationModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Locale Selection (only for new translations) */}
              {!editingTranslation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Język *
                  </label>
                  <select
                    value={formData.locale}
                    onChange={(e) => setFormData(prev => ({ ...prev, locale: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {SUPPORTED_LOCALES
                      .filter(locale => !translations.find(t => t.locale === locale.code) || locale.code === formData.locale)
                      .map(locale => (
                        <option key={locale.code} value={locale.code}>
                          {locale.flag} {locale.name} ({locale.code.toUpperCase()})
                        </option>
                      ))
                    }
                  </select>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nazwa produktu * <span className="text-gray-400 font-normal">(wymagane)</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Wpisz nazwę produktu..."
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opis
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Szczegółowy opis produktu..."
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  URL Slug
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, slug: generateSlug(prev.name) }))}
                    className="ml-2 text-xs text-blue-600 hover:text-blue-700 flex items-center"
                    disabled={!formData.name}
                  >
                    <SparklesIcon className="h-3 w-3 mr-1" />
                    Auto-generuj
                  </button>
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="produkt-slug"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Zostawi puste aby automatycznie wygenerować z nazwy
                </p>
              </div>

              {/* Meta Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Title (SEO)
                </label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Tytuł strony w wynikach wyszukiwania..."
                  maxLength={60}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.meta_title.length}/60 znaków (optymalne: 50-60)
                </p>
              </div>

              {/* Meta Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description (SEO)
                </label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Krótki opis strony w wynikach wyszukiwania..."
                  maxLength={160}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.meta_description.length}/160 znaków (optymalne: 150-160)
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => setShowTranslationModal(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={handleSaveTranslation}
                disabled={savingTranslation || !formData.name.trim()}
                className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {savingTranslation ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Zapisywanie...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Zapisz tłumaczenie
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Translations;
